import React, { useEffect, useState, useRef } from 'react';
import { useIncidents } from '../../hooks/useIncidents';
import { formatShortTimestamp, getRoomDisplayMeta } from '../../utils';

const ESTADO_LABELS = {
  abierto:     { label: 'Abierto',      css: 'incident-estado-abierto' },
  en_progreso: { label: 'En Progreso',  css: 'incident-estado-progreso' },
  atendido:    { label: 'Atendido',     css: 'incident-estado-atendido' },
};

const ESTADO_ICON = {
  abierto:     '🔴',
  en_progreso: '🟡',
  atendido:    '✅',
};

const LEVEL_LABELS = { critical: 'Crítica', warning: 'Advertencia' };

function CommentBubble({ comment }) {
  const estadoCfg = comment.estado_cambio ? ESTADO_LABELS[comment.estado_cambio] : null;
  return (
    <div className="incident-comment">
      <div className="incident-comment-meta">
        <span className="incident-comment-author">{comment.usuario_nombre}</span>
        <span className="incident-comment-time">{formatShortTimestamp(comment.created_at)}</span>
        {estadoCfg && (
          <span className={`incident-estado-chip ${estadoCfg.css}`}>
            {ESTADO_ICON[comment.estado_cambio]} {estadoCfg.label}
          </span>
        )}
      </div>
      <p className="incident-comment-body">{comment.contenido}</p>
    </div>
  );
}

function EstadoTimeline({ estado }) {
  const steps = ['abierto', 'en_progreso', 'atendido'];
  const idx = steps.indexOf(estado);
  return (
    <div className="incident-timeline-steps">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`incident-step ${i <= idx ? 'done' : ''} ${s === estado ? 'active' : ''}`}>
            <div className="incident-step-dot">{i < idx ? '✓' : i === idx ? ESTADO_ICON[s] : ''}</div>
            <div className="incident-step-label">{ESTADO_LABELS[s].label}</div>
          </div>
          {i < steps.length - 1 && <div className={`incident-step-line ${i < idx ? 'done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export function IncidentPanel({ alert, onClose }) {
  const { incident, saving, error, findActiveIncident, create, addComment, reset } = useIncidents();
  const [mode, setMode] = useState('loading'); // 'loading' | 'create' | 'detail'
  const [commentText, setCommentText] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);
  const commentsEndRef = useRef(null);

  useEffect(() => {
    if (!alert) return;
    reset();
    setMode('loading');
    setCommentText('');
    setPendingStatus(null);

    findActiveIncident(alert.roomCode, alert.sensorKey).then((found) => {
      setMode(found ? 'detail' : 'create');
    });
  }, [alert, findActiveIncident, reset]);

  // Scroll to bottom of comments when they update
  useEffect(() => {
    if (mode === 'detail') {
      commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [incident?.comments?.length, mode]);

  const handleCreate = async () => {
    if (!commentText.trim()) return;
    await create({
      room_code:        alert.roomCode,
      sensor_key:       alert.sensorKey,
      sensor_label:     alert.sensorLabel,
      category:         alert.category,
      level:            alert.level,
      value_label:      alert.valueLabel,
      threshold_detail: alert.thresholdDetail,
      initial_comment:  commentText.trim(),
    });
    setCommentText('');
    setMode('detail');
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !incident) return;
    await addComment(incident.id, commentText.trim(), pendingStatus);
    setCommentText('');
    setPendingStatus(null);
  };

  if (!alert) return null;

  return (
    <>
      <div className="incident-overlay" onClick={onClose} />
      <aside className="incident-panel">

        {/* Header */}
        <div className="incident-panel-header">
          <div className="incident-panel-header-top">
            <div className="incident-panel-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22c1.1 0 2-.9 2-2H10a2 2 0 0 0 2 2z"/>
                <path d="M18 16v-5a6 6 0 0 0-5-5.91V4a1 1 0 0 0-2 0v1.09A6 6 0 0 0 6 11v5l-2 2v1h16v-1l-2-2z"/>
              </svg>
              Seguimiento de Alerta
            </div>
            <button className="incident-panel-close" onClick={onClose} aria-label="Cerrar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          {/* Alert info card */}
          <div className={`incident-alert-card ${alert.level}`}>
            <div className="incident-alert-card-top">
              <span className={`incident-level-badge ${alert.level}`}>
                {alert.level === 'critical' ? '🚨' : '⚠️'} {LEVEL_LABELS[alert.level] || alert.level}
              </span>
              <span className="incident-alert-room">{getRoomDisplayMeta(alert.roomCode).displayName}</span>
            </div>
            <div className="incident-alert-sensor">{alert.sensorLabel}</div>
            <div className="incident-alert-value">{alert.valueLabel}</div>
            <div className="incident-alert-threshold">{alert.thresholdDetail}</div>
            <div className="incident-alert-action">{alert.action}</div>
          </div>
        </div>

        {/* Body */}
        <div className="incident-panel-body">

          {/* Loading */}
          {mode === 'loading' && (
            <div className="incident-loading">
              <div className="incident-spinner" />
              <span>Verificando incidentes activos…</span>
            </div>
          )}

          {/* Create mode */}
          {mode === 'create' && (
            <div className="incident-create-section">
              <div className="incident-create-header">
                <div className="incident-create-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                </div>
                <div>
                  <div className="incident-create-title">Abrir nuevo seguimiento</div>
                  <div className="incident-create-hint">Describe la situación inicial para registrar este incidente.</div>
                </div>
              </div>

              <div className="incident-textarea-wrap">
                <textarea
                  className="incident-textarea"
                  placeholder="Ej: Se detectó caída de oxígeno disuelto en piscina 3. Se procedió a revisar el sistema de aireación..."
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  rows={4}
                  maxLength={1000}
                />
                <div className="incident-textarea-count">{commentText.length}/1000</div>
              </div>

              {error && <div className="incident-error">{error}</div>}

              <button
                className="incident-btn-primary"
                onClick={handleCreate}
                disabled={saving || !commentText.trim()}
              >
                {saving ? (
                  <><div className="incident-btn-spinner" /> Registrando…</>
                ) : (
                  <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><path d="M12 5v14M5 12h14"/></svg> Abrir Seguimiento</>
                )}
              </button>
            </div>
          )}

          {/* Detail mode */}
          {mode === 'detail' && incident && (
            <>
              {/* Status timeline — compact row */}
              <div className="incident-status-section">
                <EstadoTimeline estado={incident.estado} />
                <div className="incident-meta-row">
                  <span className="incident-meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:12,height:12}}>
                      <circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 1 0-16 0"/>
                    </svg>
                    {incident.created_by_name}
                  </span>
                  {incident.resuelto_at && (
                    <span className="incident-meta-item incident-meta-green">
                      ✓ {incident.resuelto_by_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Comments timeline */}
              <div className="incident-section-label">
                Bitácora
                <span className="incident-comments-count">{incident.comments?.length || 0}</span>
              </div>
              <div className="incident-comments-list">
                {incident.comments?.map(c => (
                  <CommentBubble key={c.id} comment={c} />
                ))}
                <div ref={commentsEndRef} />
              </div>

              {/* Add comment — only if not resolved */}
              {incident.estado !== 'atendido' && (
                <div className="incident-add-comment">
                  <div className="incident-section-label">Agregar actualización</div>

                  {/* Status change buttons */}
                  <div className="incident-status-btns">
                    {['abierto', 'en_progreso', 'atendido']
                      .filter(s => s !== incident.estado)
                      .map(s => (
                        <button
                          key={s}
                          className={`incident-status-btn ${pendingStatus === s ? 'selected' : ''} ${s}`}
                          onClick={() => setPendingStatus(prev => prev === s ? null : s)}
                        >
                          {ESTADO_ICON[s]} Cambiar a {ESTADO_LABELS[s].label}
                        </button>
                      ))}
                  </div>

                  <div className="incident-textarea-wrap">
                    <textarea
                      className="incident-textarea"
                      placeholder="Escribe una actualización sobre el incidente..."
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      rows={3}
                      maxLength={1000}
                    />
                    <div className="incident-textarea-count">{commentText.length}/1000</div>
                  </div>

                  {error && <div className="incident-error">{error}</div>}

                  <button
                    className={`incident-btn-primary ${pendingStatus === 'atendido' ? 'success' : ''}`}
                    onClick={handleAddComment}
                    disabled={saving || !commentText.trim()}
                  >
                    {saving ? (
                      <><div className="incident-btn-spinner" /> Guardando…</>
                    ) : pendingStatus === 'atendido' ? (
                      <>✓ Marcar como Atendido</>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}>
                          <path d="M22 2 11 13M22 2 15 22l-4-9-9-4 20-7z"/>
                        </svg>
                        {pendingStatus ? `Enviar y cambiar a ${ESTADO_LABELS[pendingStatus].label}` : 'Enviar actualización'}
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Resolved message */}
              {incident.estado === 'atendido' && (
                <div className="incident-resolved-banner">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <div>
                    <strong>Incidente atendido</strong>
                    <span>por {incident.resuelto_by_name} · {formatShortTimestamp(incident.resuelto_at)}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
